import { SUPPORT_EMAIL } from '@core/constants';

export type CognitoMessageType = 'success' | 'warning' | 'danger';

export interface CognitoFriendlyMessage {
  type: CognitoMessageType;
  message: string;
  messageKey: string;
}

export type CognitoContext =
  | 'signIn'
  | 'signUp'
  | 'signUpConfirm'
  | 'signUpResend'
  | 'resendCode'
  | 'confirmCode';

export function getFriendlyCognitoMessage(
  context: CognitoContext,
  code: string,
  message?: string
): CognitoFriendlyMessage {
  switch (context) {
    case 'signIn':
      return getSignInMessage(code, message);
    case 'signUp':
      return getSignUpMessage(code, message);
    case 'signUpConfirm':
      return getSignUpConfirmMessage(code, message);
    case 'signUpResend':
      return getSignUpResendMessage(code, message);
    case 'resendCode':
      return getResendMessage(code, message);
    case 'confirmCode':
      return getConfirmCodeMessage(code, message);
    default:
      return {
        type: 'danger',
        message: message || 'An unexpected error occurred. Please try again.',
        messageKey: 'error.generic'
      };
  }
}

export function getFederatedSignInMessage(
  error?: string | null,
  description?: string | null
): CognitoFriendlyMessage {
  const detail = (description ?? '').toLowerCase();

  if (error === 'access_denied' && /cancel|declin/.test(detail)) {
    return make('auth.sso.cancelled', 'warning', 'Sign-in was cancelled. Please try again.');
  }

  if (/attribute|email|saml|claim|nameid/.test(detail)) {
    return make(
      'auth.sso.missingAttributes',
      'danger',
      "Your organisation's sign-in didn't return the details we need (such as your email). " +
        `Please contact your administrator or ${SUPPORT_EMAIL}.`
    );
  }

  if (error === 'invalid_grant' || /expired|invalid_grant/.test(detail)) {
    return make('auth.sso.expired', 'warning', 'Your sign-in session expired. Please try again.');
  }

  if (error === 'access_denied') {
    return make(
      'auth.sso.accessDenied',
      'danger',
      'Your organisation declined the sign-in, or your account is not authorised. ' +
        'Please contact your administrator.'
    );
  }

  return make(
    'auth.sso.unknown',
    'danger',
    'We could not sign you in through your organisation. Please try again, or contact ' +
      `${SUPPORT_EMAIL} if it continues.`
  );
}

function make(key: string, type: CognitoMessageType, fallback: string): CognitoFriendlyMessage {
  return { messageKey: key, type, message: fallback };
}

function getSignInMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    case 'UserNotFoundException':
    case 'NotAuthorizedException':
      return make('auth.signIn.invalidCredentials', 'danger', 'Invalid email or password.');
    case 'UserNotConfirmedException':
      return make(
        'auth.signIn.unverified',
        'warning',
        'Your account is not verified. Check your email or phone for instructions.'
      );
    case 'PasswordResetRequiredException':
      return make(
        'auth.signIn.resetRequired',
        'warning',
        'Password reset required. Follow the instructions sent to your email.'
      );
    case 'InvalidParameterException':
      return make(
        'auth.signIn.invalidInput',
        'danger',
        'Invalid input. Please check your email and password.'
      );
    case 'LimitExceededException':
    case 'TooManyFailedAttemptsException':
      return make(
        'auth.signIn.tooManyAttempts',
        'warning',
        'Too many attempts. Please wait and try again later.'
      );
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Network issue. Check your internet connection.');
    default:
      return make(
        'auth.signIn.unknown',
        'danger',
        message || 'Unable to sign in. Please try again.'
      );
  }
}

function getSignUpMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    case 'UsernameExistsException':
      return make(
        'auth.signUp.userExists',
        'warning',
        'An account may already exist. Try signing in instead.'
      );
    case 'InvalidPasswordException':
      return make(
        'auth.signUp.weakPassword',
        'danger',
        'Password does not meet complexity requirements.'
      );
    case 'InvalidParameterException':
      return make(
        'auth.signUp.invalidInput',
        'danger',
        'Some information is missing or invalid. Please try again.'
      );
    case 'CodeDeliveryFailureException':
      return make(
        'auth.signUp.codeDeliveryFailed',
        'danger',
        'We couldn’t send the confirmation code. Check your email or contact support.'
      );
    case 'LimitExceededException':
      return make(
        'auth.signUp.tooManyRequests',
        'warning',
        'Too many attempts. Please wait before trying again.'
      );
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Network issue. Please try again.');
    default:
      return make(
        'auth.signUp.unknown',
        'danger',
        message || 'Unable to complete registration. Please try again.'
      );
  }
}

function getSignUpConfirmMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    case 'CodeMismatchException':
      return make('auth.signUpConfirm.codeMismatch', 'danger', 'Incorrect code. Please try again.');
    case 'ExpiredCodeException':
      return make('auth.signUpConfirm.expiredCode', 'danger', 'Code expired. Request a new one.');
    case 'LimitExceededException':
      return make(
        'auth.signUpConfirm.tooManyAttempts',
        'warning',
        'Too many attempts. Try again later.'
      );
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Network issue. Please try again.');
    default:
      return make(
        'auth.signUpConfirm.unknown',
        'danger',
        message || 'Unable to verify your account. Please try again.'
      );
  }
}

function getSignUpResendMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    case 'UserNotFoundException':
      return make(
        'auth.signUpResend.sentIfExists',
        'success',
        'If the account exists, a confirmation code was sent.'
      );
    case 'CodeDeliveryFailureException':
      return make(
        'auth.signUpResend.codeDeliveryFailed',
        'danger',
        'We couldn’t send the confirmation code. Try again.'
      );
    case 'LimitExceededException':
      return make(
        'auth.signUpResend.tooManyRequests',
        'warning',
        'Too many requests. Please wait and try again.'
      );
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Network issue. Please try again.');
    default:
      return make(
        'auth.signUpResend.unknown',
        'danger',
        message || 'Unable to resend code. Please try again.'
      );
  }
}

function getResendMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    case 'UserNotFoundException':
      return make(
        'auth.resend.sentIfExists',
        'success',
        'If your account exists, a new code has been sent.'
      );
    case 'CodeDeliveryFailureException':
      return make(
        'auth.resend.codeDeliveryFailed',
        'danger',
        'We couldn’t send the code. Please verify your email.'
      );
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return make('auth.resend.tooManyRequests', 'warning', 'Too many requests. Try again later.');
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Network issue. Please try again.');
    default:
      return make(
        'auth.resend.unknown',
        'danger',
        message || 'Unable to resend code. Please try again.'
      );
  }
}

function getConfirmCodeMessage(code: string, message?: string): CognitoFriendlyMessage {
  switch (code) {
    // Enumeration safety: an already-confirmed (NotAuthorizedException) or non-existent
    // (UserNotFoundException) account must return the same "wrong code" response as a genuine code
    // mismatch, so the confirm step reveals no account state to the unauthenticated resend affordance.
    case 'CodeMismatchException':
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return make('auth.confirmCode.codeMismatch', 'danger', 'Incorrect code. Try again.');
    case 'ExpiredCodeException':
      return make('auth.confirmCode.expiredCode', 'danger', 'Code expired. Request a new one.');
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return make(
        'auth.confirmCode.tooManyAttempts',
        'warning',
        'Too many attempts. Please try again later.'
      );
    case 'NetworkError':
      return make('auth.networkError', 'danger', 'Check your connection and try again.');
    default:
      return make(
        'auth.confirmCode.unknown',
        'danger',
        message || 'Could not confirm your code. Try again.'
      );
  }
}
